import React, { useState, useEffect, useCallback } from 'react';
import {
  Send, Clock, CheckCircle, AlertTriangle, RefreshCw, X, ChevronDown,
  ChevronUp, Edit3, Calendar, Users, MessageSquare, Bell, Zap,
  Filter, CheckSquare, Square, Eye, Save, Trash2
} from 'lucide-react';
import escalationService from '../../../services/escalationService';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


// ─── Default Templates ────────────────────────────────────────────────────────
const DEFAULT_TEMPLATES = [
  {
    id: 'tpl-1',
    name: 'Rappel Standard',
    subject: 'Rappel : Demande client en attente de votre réponse',
    body: `Bonjour {{professional_name}},

Nous vous contactons car un(e) client(e) attend toujours votre réponse concernant {{inquiry_type}} depuis {{hours_elapsed}}h.

Détails de la demande :
- Client : {{client_name}}
- Type : {{inquiry_type}}
- Date de la demande : {{inquiry_date}}

Merci de bien vouloir traiter cette demande dans les plus brefs délais afin de maintenir la qualité de service de la plateforme Kapital States.

Cordialement,
L'équipe Kapital States`,tone: 'neutral',
  },
  {
    id: 'tpl-2',name: 'Rappel Urgent',subject: '⚠️ URGENT — Demande escaladée nécessitant votre attention immédiate',
    body: `Bonjour {{professional_name}},

ATTENTION : La demande de {{client_name}} est escaladée en urgence après {{hours_elapsed}}h sans réponse de votre part.

Cette situation impacte la réputation de votre profil sur Kapital States. Une absence de réponse prolongée peut entraîner une suspension temporaire de votre compte.

Demande concernée :
- Type : {{inquiry_type}}
- Date : {{inquiry_date}}
- Statut : URGENT

Veuillez répondre immédiatement via votre espace professionnel.

L'équipe Kapital States`,
    tone: 'urgent',
  },
  {
    id: 'tpl-3',
    name: 'Rappel Cordial',
    subject: 'Un client attend votre retour — Kapital States',
    body: `Bonjour {{professional_name}},

Nous espérons que vous allez bien. Nous vous rappelons qu'un(e) client(e) a soumis une demande il y a {{hours_elapsed}}h et attend toujours votre réponse.

Informations :
- Nom du client : {{client_name}}
- Type de demande : {{inquiry_type}}

Votre réactivité est l'un des critères clés de votre évaluation sur notre plateforme. N'hésitez pas à nous contacter si vous rencontrez des difficultés.

Merci et bonne journée,
L'équipe Kapital States`,
    tone: 'friendly',
  },
];

const TONE_LABELS = { neutral: 'Neutre', urgent: 'Urgent', friendly: 'Cordial' };
const TONE_COLORS = {
  neutral: 'bg-blue-100 text-blue-700',
  urgent: 'bg-red-100 text-red-700',
  friendly: 'bg-green-100 text-green-700',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const hoursElapsed = (date) => {
  if (!date) return '?';
  return Math.round((Date.now() - new Date(date)?.getTime()) / (1000 * 60 * 60));
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const TemplateEditor = ({ template, onChange, onSave, onCancel }) => {
  const [local, setLocal] = useState({ ...template });

  const update = (field, val) => setLocal((p) => ({ ...p, [field]: val }));

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
          <Edit3 className="w-4 h-4 text-blue-600" />
          Modifier le template
        </h4>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Nom du template</label>
          <input
            value={local?.name}
            onChange={(e) => update('name', e?.target?.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Ton</label>
          <select
            value={local?.tone}
            onChange={(e) => update('tone', e?.target?.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="neutral">Neutre</option>
            <option value="urgent">Urgent</option>
            <option value="friendly">Cordial</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Objet de l'email</label>
        <input
          value={local?.subject}
          onChange={(e) => update('subject', e?.target?.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Corps du message
          <span className="ml-2 text-gray-400 font-normal">
            Variables : {'{{professional_name}}'}, {'{{client_name}}'}, {'{{inquiry_type}}'}, {'{{hours_elapsed}}'}, {'{{inquiry_date}}'}
          </span>
        </label>
        <textarea
          value={local?.body}
          onChange={(e) => update('body', e?.target?.value)}
          rows={8}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono"
        />
      </div>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors">
          Annuler
        </button>
        <button
          onClick={() => { onSave(local); onCancel(); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <Save className="w-4 h-4" />
          Enregistrer
        </button>
      </div>
    </div>
  );
};

const PreviewModal = ({ template, item, type, onClose }) => {
  const professional_name = item?.professional_name || item?.professional_id?.slice(0, 8) || 'Professionnel';
  const client_name = type === 'booking' ? item?.client_name : item?.client_name || item?.client_email || 'Client';
  const inquiry_type = type === 'booking' ? `Réservation (${item?.service_type || 'service'})` : `Demande propriété`;
  const hours = hoursElapsed(item?.escalated_at || item?.created_at);
  const inquiry_date = formatDate(item?.created_at);

  const render = (text) =>
    text
      ?.replace(/{{professional_name}}/g, professional_name)
      ?.replace(/{{client_name}}/g, client_name)
      ?.replace(/{{inquiry_type}}/g, inquiry_type)
      ?.replace(/{{hours_elapsed}}/g, hours)
      ?.replace(/{{inquiry_date}}/g, inquiry_date);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
              <Eye className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Aperçu du message</h3>
              <p className="text-xs text-gray-500">{template?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-500 mb-1">OBJET</p>
            <p className="text-sm font-medium text-gray-800">{render(template?.subject)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-500 mb-2">CORPS</p>
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{render(template?.body)}</pre>
          </div>
        </div>
        <div className="p-4 border-t border-gray-200">
          <button onClick={onClose} className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
            Fermer l'aperçu
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const BatchReminderTool = () => {
  const [urgentBookings, setUrgentBookings] = useState([]);
  const [urgentInquiries, setUrgentInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [selectedTemplateId, setSelectedTemplateId] = useState('tpl-1');
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [filterType, setFilterType] = useState('all'); // all | bookings | inquiries
  const [scheduleMode, setScheduleMode] = useState('now'); // now | scheduled
  const [scheduledAt, setScheduledAt] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [previewModal, setPreviewModal] = useState(null); // { item, type }
  const [showTemplates, setShowTemplates] = useState(true);

  const selectedTemplate = templates?.find((t) => t?.id === selectedTemplateId) || templates?.[0];

  // ── Fetch data ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bookingsRes, inquiriesRes] = await Promise.all([
        escalationService?.getUrgentBookings(),
        escalationService?.getUrgentInquiries(),
      ]);
      setUrgentBookings(bookingsRes?.data || []);
      setUrgentInquiries(inquiriesRes?.data || []);
    } catch (err) {
      console.error('[BatchReminderTool] fetchData error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived list ────────────────────────────────────────────────────────────
  const allItems = [
    ...urgentBookings?.map((b) => ({ ...b, _type: 'booking' })),
    ...urgentInquiries?.map((i) => ({ ...i, _type: 'inquiry' })),
  ];

  const filteredItems = allItems?.filter((item) => {
    if (filterType === 'bookings') return item?._type === 'booking';
    if (filterType === 'inquiries') return item?._type === 'inquiry';
    return true;
  });

  // ── Selection helpers ───────────────────────────────────────────────────────
  const toggleItem = (id) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      next?.has(id) ? next?.delete(id) : next?.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedItems?.size === filteredItems?.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems?.map((i) => i?.id)));
    }
  };

  // ── Template helpers ────────────────────────────────────────────────────────
  const saveTemplate = (updated) => {
    setTemplates((prev) => prev?.map((t) => (t?.id === updated?.id ? updated : t)));
  };

  const addTemplate = () => {
    const newTpl = {
      id: `tpl-${Date.now()}`,
      name: 'Nouveau template',
      subject: 'Rappel — Demande en attente',
      body: 'Bonjour {{professional_name}},\n\nVotre message ici.\n\nCordialement,\nL\'équipe Kapital States',
      tone: 'neutral',
    };
    setTemplates((prev) => [...prev, newTpl]);
    setEditingTemplateId(newTpl?.id);
    setSelectedTemplateId(newTpl?.id);
  };

  const deleteTemplate = (id) => {
    if (templates?.length <= 1) return;
    setTemplates((prev) => prev?.filter((t) => t?.id !== id));
    if (selectedTemplateId === id) setSelectedTemplateId(templates?.[0]?.id);
  };

  // ── Send batch ──────────────────────────────────────────────────────────────
  const handleSendBatch = async () => {
    if (selectedItems?.size === 0) return;
    setSending(true);
    setSendResult(null);

    try {
      const itemsToSend = allItems?.filter((i) => selectedItems?.has(i?.id));
      let successCount = 0;
      let failCount = 0;

      for (const item of itemsToSend) {
        try {
          const professional_name = item?.professional_name || 'Professionnel';
          const client_name = item?._type === 'booking' ? item?.client_name : item?.client_name || item?.client_email || 'Client';
          const inquiry_type = item?._type === 'booking' ? `Réservation (${item?.service_type || 'service'})` : 'Demande propriété';
          const hours = hoursElapsed(item?.escalated_at || item?.created_at);
          const inquiry_date = formatDate(item?.created_at);

          const renderText = (text) =>
            text
              ?.replace(/{{professional_name}}/g, professional_name)
              ?.replace(/{{client_name}}/g, client_name)
              ?.replace(/{{inquiry_type}}/g, inquiry_type)
              ?.replace(/{{hours_elapsed}}/g, hours)
              ?.replace(/{{inquiry_date}}/g, inquiry_date);

          // Log the reminder notification in escalation_logs
          await supabase?.from('escalation_logs')?.insert({
            entity_type: item?._type === 'booking' ? 'professional_booking' : 'property_inquiry',
            entity_id: item?.id,
            escalation_reason: `Rappel batch envoyé — Template: ${selectedTemplate?.name}`,
            escalated_at: scheduleMode === 'scheduled' && scheduledAt ? scheduledAt : new Date()?.toISOString(),
            resolved_at: null,
          });

          // Insert admin notification
          await supabase?.from('notifications')?.insert({
            type: 'reminder_sent',
            title: renderText(selectedTemplate?.subject),
            message: renderText(selectedTemplate?.body)?.slice(0, 500),
            metadata: {
              template_id: selectedTemplate?.id,
              template_name: selectedTemplate?.name,
              entity_type: item?._type,
              entity_id: item?.id,
              scheduled_at: scheduleMode === 'scheduled' ? scheduledAt : null,
            },
          });

          successCount++;
        } catch {
          failCount++;
        }
      }

      setSendResult({
        success: failCount === 0,
        message: `${successCount} rappel(s) ${scheduleMode === 'scheduled' ? 'planifié(s)' : 'envoyé(s)'} avec succès${failCount > 0 ? `, ${failCount} échec(s)` : ''}.`,
      });
      setSelectedItems(new Set());
      await fetchData();
    } finally {
      setSending(false);
      setTimeout(() => setSendResult(null), 6000);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {previewModal && (
        <PreviewModal
          template={selectedTemplate}
          item={previewModal?.item}
          type={previewModal?.type}
          onClose={() => setPreviewModal(null)}
        />
      )}
      {/* ── Header ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="w-6 h-6 text-blue-600" />
              Outil de Rappels en Lot
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Envoyez des notifications de rappel personnalisées aux professionnels non réactifs sur des demandes escaladées.
            </p>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-4 mt-5">
          {[
            { label: 'Réservations urgentes', value: urgentBookings?.length, color: 'orange', icon: Calendar },
            { label: 'Demandes urgentes', value: urgentInquiries?.length, color: 'red', icon: MessageSquare },
            { label: 'Sélectionnés', value: selectedItems?.size, color: 'blue', icon: Users },
          ]?.map(({ label, value, color, icon: Icon }) => (
            <div key={label} className={`rounded-xl border p-4 bg-${color}-50 border-${color}-200`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-semibold text-${color}-600 uppercase tracking-wide`}>{label}</span>
                <Icon className={`w-4 h-4 text-${color}-500`} />
              </div>
              <p className={`text-3xl font-bold text-${color}-700`}>{value}</p>
            </div>
          ))}
        </div>
      </div>
      {/* ── Template Section ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          onClick={() => setShowTemplates((p) => !p)}
          className="w-full flex items-center justify-between p-5 text-left"
        >
          <div className="flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-blue-600" />
            <span className="font-bold text-gray-900">Templates de messages</span>
            <span className="text-xs text-gray-400 font-normal ml-1">({templates?.length} templates)</span>
          </div>
          {showTemplates ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {showTemplates && (
          <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
            {/* Template selector */}
            <div className="flex flex-wrap gap-2">
              {templates?.map((tpl) => (
                <div key={tpl?.id} className="flex items-center gap-1">
                  <button
                    onClick={() => { setSelectedTemplateId(tpl?.id); setEditingTemplateId(null); }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      selectedTemplateId === tpl?.id
                        ? 'bg-blue-600 text-white border-blue-600' :'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {tpl?.name}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${TONE_COLORS?.[tpl?.tone]}`}>
                      {TONE_LABELS?.[tpl?.tone]}
                    </span>
                  </button>
                  <button
                    onClick={() => setEditingTemplateId(editingTemplateId === tpl?.id ? null : tpl?.id)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Modifier"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  {templates?.length > 1 && (
                    <button
                      onClick={() => deleteTemplate(tpl?.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addTemplate}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-blue-400 text-blue-600 rounded-lg text-sm hover:bg-blue-50 transition-colors"
              >
                + Nouveau template
              </button>
            </div>

            {/* Editor or preview */}
            {editingTemplateId ? (
              <TemplateEditor
                template={templates?.find((t) => t?.id === editingTemplateId)}
                onChange={(updated) => setTemplates((prev) => prev?.map((t) => (t?.id === updated?.id ? updated : t)))}
                onSave={saveTemplate}
                onCancel={() => setEditingTemplateId(null)}
              />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">OBJET</p>
                  <p className="text-sm text-gray-800 font-medium">{selectedTemplate?.subject}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">APERÇU DU CORPS</p>
                  <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans leading-relaxed line-clamp-4">
                    {selectedTemplate?.body?.slice(0, 300)}{selectedTemplate?.body?.length > 300 ? '…' : ''}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* ── Scheduling ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-blue-600" />
          <h3 className="font-bold text-gray-900">Planification de l'envoi</h3>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <label className={`flex items-center gap-3 flex-1 p-4 rounded-xl border-2 cursor-pointer transition-colors ${scheduleMode === 'now' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <input
              type="radio"
              name="schedule"
              value="now"
              checked={scheduleMode === 'now'}
              onChange={() => setScheduleMode('now')}
              className="accent-blue-600"
            />
            <div>
              <p className="font-semibold text-gray-800 text-sm">Envoyer maintenant</p>
              <p className="text-xs text-gray-500">Les rappels sont envoyés immédiatement</p>
            </div>
            <Zap className={`w-5 h-5 ml-auto ${scheduleMode === 'now' ? 'text-blue-500' : 'text-gray-300'}`} />
          </label>
          <label className={`flex items-center gap-3 flex-1 p-4 rounded-xl border-2 cursor-pointer transition-colors ${scheduleMode === 'scheduled' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <input
              type="radio"
              name="schedule"
              value="scheduled"
              checked={scheduleMode === 'scheduled'}
              onChange={() => setScheduleMode('scheduled')}
              className="accent-blue-600"
            />
            <div className="flex-1">
              <p className="font-semibold text-gray-800 text-sm">Planifier l'envoi</p>
              {scheduleMode === 'scheduled' ? (
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e?.target?.value)}
                  min={new Date()?.toISOString()?.slice(0, 16)}
                  className="mt-1.5 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-xs text-gray-500">Choisir une date et heure d'envoi</p>
              )}
            </div>
            <Calendar className={`w-5 h-5 ml-auto ${scheduleMode === 'scheduled' ? 'text-blue-500' : 'text-gray-300'}`} />
          </label>
        </div>
      </div>
      {/* ── Item Selection ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-5 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-gray-900">Sélection des destinataires</h3>
              <span className="text-xs text-gray-400">({filteredItems?.length} demandes escaladées)</span>
            </div>
            <div className="flex items-center gap-2">
              {['all', 'bookings', 'inquiries']?.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilterType(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filterType === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'Tous' : f === 'bookings' ? 'Réservations' : 'Demandes'}
                </button>
              ))}
            </div>
          </div>

          {/* Select all row */}
          {filteredItems?.length > 0 && (
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
              <button onClick={toggleAll} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
                {selectedItems?.size === filteredItems?.length ? (
                  <CheckSquare className="w-4 h-4" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {selectedItems?.size === filteredItems?.length ? 'Tout désélectionner' : 'Tout sélectionner'}
              </button>
              {selectedItems?.size > 0 && (
                <span className="text-xs text-gray-500">{selectedItems?.size} sélectionné(s)</span>
              )}
            </div>
          )}
        </div>

        {filteredItems?.length === 0 ? (
          <div className="py-16 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Aucune demande escaladée en attente</p>
            <p className="text-sm text-gray-400 mt-1">Tous les professionnels ont répondu à leurs demandes.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredItems?.map((item) => {
              const isSelected = selectedItems?.has(item?.id);
              const hours = hoursElapsed(item?.escalated_at || item?.created_at);
              const isBooking = item?._type === 'booking';
              return (
                <div
                  key={item?.id}
                  onClick={() => toggleItem(item?.id)}
                  className={`flex items-start gap-4 p-4 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {isSelected ? (
                      <CheckSquare className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${isBooking ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}`}>
                        <AlertTriangle className="w-3 h-3" />
                        {isBooking ? 'Réservation' : 'Demande propriété'}
                      </span>
                      <span className={`text-xs font-medium ${hours >= 48 ? 'text-red-600' : 'text-orange-600'}`}>
                        {hours}h sans réponse
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 mt-1 truncate">
                      {isBooking
                        ? `${item?.client_name || 'Client'} — ${item?.service_type || 'Service'}`
                        : `${item?.client_name || item?.client_email || 'Client'} — ${item?.property?.title || 'Propriété'}`}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Professionnel : {item?.professional_name || item?.professional_id?.slice(0, 12) || '—'} · Créé le {formatDate(item?.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e?.stopPropagation(); setPreviewModal({ item, type: item?._type }); }}
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Aperçu du message"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* ── Send Bar ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        {sendResult && (
          <div className={`mb-4 p-3 rounded-lg text-sm font-medium flex items-center gap-2 ${sendResult?.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {sendResult?.success ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
            {sendResult?.message}
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            {selectedItems?.size === 0 ? (
              <span className="text-gray-400">Sélectionnez des destinataires pour envoyer les rappels.</span>
            ) : (
              <span>
                <strong className="text-gray-900">{selectedItems?.size}</strong> professionnel(s) sélectionné(s) ·{' '}
                Template : <strong className="text-blue-700">{selectedTemplate?.name}</strong> ·{' '}
                {scheduleMode === 'now' ? 'Envoi immédiat' : scheduledAt ? `Planifié le ${new Date(scheduledAt)?.toLocaleString('fr-FR')}` : 'Date non définie'}
              </span>
            )}
          </div>
          <button
            onClick={handleSendBatch}
            disabled={selectedItems?.size === 0 || sending || (scheduleMode === 'scheduled' && !scheduledAt)}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {scheduleMode === 'scheduled' ? 'Planifier les rappels' : 'Envoyer les rappels'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchReminderTool;
